import React, { useEffect } from 'react';
import { ENGAGEMENT_PHASES } from '../data/stitchData';
import logo from '../assets/logo.png';

export default function ServiceDetailModal({ service, onClose, onScheduleClick }) {
  useEffect(() => {
    if (!service) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [service, onClose]);

  if (!service) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5 lg:p-8">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-950/75 backdrop-blur-md transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal Card with max-w-5xl for balanced 2-column architecture */}
      <div className="relative z-10 w-full max-w-5xl max-h-[92vh] overflow-y-auto bg-white dark:bg-clinical-slate-900 rounded-3xl border border-gray-200/90 dark:border-gray-800 shadow-[0_32px_72px_-12px_rgba(0,0,0,0.55)] flex flex-col">
        {/* Floating Close Button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close modal"
          className="absolute top-4 right-4 z-30 w-10 h-10 rounded-full bg-gray-100/90 dark:bg-clinical-slate-800/90 hover:bg-primary hover:text-white dark:hover:bg-primary text-gray-700 dark:text-gray-200 flex items-center justify-center transition-all shadow-md backdrop-blur-md"
        >
          <span className="material-symbols-outlined text-[20px]">close</span>
        </button>

        {/* Executive Split Header: Left Title & Info, Right Uncropped 16:9 Hero Spotlight */}
        <div className="relative p-6 sm:p-8 lg:p-10 border-b border-gray-200/80 dark:border-gray-800 bg-gradient-to-br from-blue-50/70 via-white to-gray-50/40 dark:from-clinical-slate-800/90 dark:via-clinical-slate-900 dark:to-clinical-slate-900">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 pr-8">
            
            {/* Left side: Category badge, Title, Subtitle */}
            <div className="flex-1 space-y-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 dark:bg-primary/20 text-primary dark:text-blue-300 border border-primary/20 text-xs font-bold uppercase tracking-wider">
                <span className="material-symbols-outlined text-[16px]">{service.icon}</span>
                <span>{service.tierName || 'Healthcare Advisory'} • {service.category}</span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white leading-tight">
                {service.title}
              </h2>

              <p className="text-sm sm:text-base text-gray-600 dark:text-slate-300 leading-relaxed max-w-2xl">
                {service.subtitle}
              </p>
            </div>

            {/* Right side: Natural 16:9 Uncropped Hero Image */}
            <div className="w-full lg:w-80 xl:w-96 shrink-0">
              <div className="relative rounded-2xl overflow-hidden border border-gray-200/80 dark:border-gray-700/80 shadow-md aspect-[16/9] group bg-gray-900">
                <img
                  src={service.heroImg}
                  alt={service.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950/30 to-transparent pointer-events-none" />
              </div>
            </div>

          </div>
        </div>

        {/* Modal Content Body */}
        <div className="p-6 sm:p-8 lg:p-10 space-y-8">
          
          {/* Executive Metadata Snapshot Strip */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3.5 rounded-2xl bg-gray-50/90 dark:bg-clinical-slate-800/60 border border-gray-200/80 dark:border-gray-700/70">
            <div className="flex items-start gap-3 p-1.5">
              <div className="w-9 h-9 rounded-xl bg-primary/10 dark:bg-primary/20 text-primary dark:text-blue-300 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-[19px]">badge</span>
              </div>
              <div className="min-w-0">
                <span className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400 block tracking-wider">
                  Target Audience
                </span>
                <p className="text-xs font-semibold text-gray-900 dark:text-white leading-tight mt-0.5">
                  {service.audience}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-1.5 sm:border-l sm:border-gray-200/70 dark:sm:border-gray-700/70 sm:pl-4">
              <div className="w-9 h-9 rounded-xl bg-accent-teal/10 dark:bg-accent-teal/20 text-accent-teal flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-[19px]">verified_user</span>
              </div>
              <div className="min-w-0">
                <span className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400 block tracking-wider">
                  Compliance &amp; Standing
                </span>
                <p className="text-xs font-semibold text-gray-900 dark:text-white leading-tight mt-0.5">
                  {service.accreditation}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-1.5 sm:border-l sm:border-gray-200/70 dark:sm:border-gray-700/70 sm:pl-4">
              <div className="w-9 h-9 rounded-xl bg-secondary/10 dark:bg-secondary/20 text-secondary dark:text-blue-300 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-[19px]">schedule</span>
              </div>
              <div className="min-w-0">
                <span className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400 block tracking-wider">
                  Engagement Horizon
                </span>
                <p className="text-xs font-semibold text-gray-900 dark:text-white leading-tight mt-0.5">
                  {service.timeline}
                </p>
              </div>
            </div>
          </div>

          {/* 2-Column Symmetrical Grid: Left (Outcomes & Scenario) + Right (Roadmap) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            
            {/* Left Column (7 cols): Clinical Outcomes & Real World Case Brief */}
            <div className="lg:col-span-7 flex flex-col justify-between space-y-6">
              
              {/* Expected Clinical Outcomes */}
              <div className="space-y-3.5">
                <div className="flex items-center justify-between">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary dark:text-blue-300 text-[22px]">trending_up</span>
                    <span>Expected Outcomes &amp; Clinical Impact</span>
                  </h3>
                  <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Verified Benchmarks
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {service.outcomes.map((item, oIdx) => (
                    <div
                      key={oIdx}
                      className="p-4 rounded-2xl bg-gray-50/70 dark:bg-clinical-slate-800/50 border border-gray-200/70 dark:border-gray-700/60 hover:border-primary/40 transition-colors"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="material-symbols-outlined text-primary dark:text-blue-300 text-[18px]">
                          check_circle
                        </span>
                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                          {item.stat}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-slate-300 leading-relaxed pl-6.5">
                        {item.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Clean Centered Borderless Logo & Name */}
              <div className="py-3 sm:py-4 flex items-center justify-center gap-4 sm:gap-6 text-center">
                <img
                  src={logo}
                  alt="NFBG Scorpio"
                  className="h-16 sm:h-20 lg:h-22 w-auto object-contain drop-shadow-md transition-transform duration-300 hover:scale-105 shrink-0"
                />
                <span className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-gray-900 dark:text-white leading-none">
                  NFBG <span className="text-primary font-bold">SCORPIO</span>
                </span>
              </div>

              {/* Real World Hospital Case Briefing */}
              <div className="p-5 rounded-2xl bg-gradient-to-br from-ice-cyan/40 via-surface-container-low/50 to-transparent dark:from-clinical-slate-800/70 dark:via-clinical-slate-800/40 dark:to-transparent border-l-4 border-primary border-t border-r border-b border-gray-200/80 dark:border-gray-700/80 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-wider font-bold text-primary dark:text-blue-300 flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-[16px]">clinical_notes</span>
                    <span>Representative Health System Scenario</span>
                  </span>
                  <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                    Historical Casework
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-gray-700 dark:text-slate-200 leading-relaxed italic">
                  "{service.scenario}"
                </p>
              </div>

            </div>

            {/* Right Column (5 cols): Engagement Timeline Protocol */}
            <div className="lg:col-span-5 flex flex-col">
              <div className="p-6 rounded-2xl bg-gray-50/80 dark:bg-clinical-slate-800/60 border border-gray-200/80 dark:border-gray-700/70 space-y-4 h-full flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between border-b border-gray-200/60 dark:border-gray-700/60 pb-3 mb-4">
                    <h4 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary dark:text-blue-300 text-[20px]">account_tree</span>
                      <span>Engagement Protocol</span>
                    </h4>
                    <span className="text-[10px] font-bold text-accent-teal uppercase tracking-widest">
                      Structured Roadmap
                    </span>
                  </div>

                  {/* Connected Vertical Timeline */}
                  <div className="relative pl-6 space-y-5 before:absolute before:left-2 before:top-2.5 before:bottom-2.5 before:w-0.5 before:bg-gray-200 dark:before:bg-gray-700">
                    {ENGAGEMENT_PHASES.map((p, pIdx) => (
                      <div key={pIdx} className="relative">
                        {/* Node Circle */}
                        <div className="absolute -left-6 top-0.5 w-4.5 h-4.5 rounded-full bg-white dark:bg-clinical-slate-900 border-2 border-primary text-primary flex items-center justify-center shadow-xs">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                        </div>
                        <div>
                          <span className="text-[10px] uppercase font-bold tracking-wider text-primary dark:text-blue-300">
                            {p.phase}
                          </span>
                          <h5 className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white mt-0.5">
                            {p.title}
                          </h5>
                          <p className="text-xs text-gray-600 dark:text-slate-300 mt-1 leading-relaxed">
                            {p.desc}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-3 border-t border-gray-200/50 dark:border-gray-700/50 flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                  <span className="material-symbols-outlined text-accent-teal text-[16px]">verified</span>
                  <span>Facilitated by certified neutral healthcare mediators</span>
                </div>
              </div>
            </div>

          </div>

          {/* Full Width Bottom Consultation Action Bar (Eliminates bottom-left negative space) */}
          <div className="p-6 rounded-2xl bg-gradient-to-r from-primary/10 via-primary/5 to-accent-teal/10 dark:from-clinical-slate-800 dark:via-clinical-slate-800/90 dark:to-clinical-slate-800/70 border border-primary/20 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-5 text-center sm:text-left shadow-sm">
            <div className="space-y-1">
              <span className="text-[10px] uppercase tracking-wider font-bold text-primary dark:text-blue-300 block">
                Statutory Confidentiality
              </span>
              <h4 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                Initiate Confidential Consultation
              </h4>
              <p className="text-xs text-gray-600 dark:text-slate-300 leading-relaxed max-w-xl">
                Operating strictly under Evidence Code § 1157 statutory privilege and healthcare peer review protections.
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                onClose();
                if (onScheduleClick) onScheduleClick(service.title);
              }}
              className="shrink-0 inline-flex items-center justify-center gap-2 py-3.5 px-7 rounded-full bg-primary hover:bg-primary-container text-white font-semibold text-xs sm:text-sm shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all"
            >
              <span>Schedule Confidential Briefing</span>
              <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
